"use client";
import React, { useState } from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const ACCENT_LIGHT = '#818cf8';
const CARD = '#fbfdfd';

const items = [
    {
        title: 'Google Business Profile Optimisation',
        featured: true,
        span: 'md:col-span-3 md:row-span-2',
        icon: (
            <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
                <path d="M16 29s10-9.5 10-16.5A10 10 0 1 0 6 12.5C6 19.5 16 29 16 29z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <circle cx="16" cy="12.5" r="3.5" stroke="currentColor" strokeWidth="2" />
            </svg>
        ),
        desc: 'We optimise your Business Profile around relevant categories, services, business information, photos and customer-facing content.',
    },
    {
        title: 'Local Keyword Strategy',
        span: 'md:col-span-3',
        icon: (
            <svg width="26" height="26" viewBox="0 0 32 32" fill="none">
                <circle cx="14" cy="14" r="8" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We identify the searches your potential customers use when looking for your products or services in specific locations.',
    },
    {
        title: 'Local On-Page SEO',
        span: 'md:col-span-3',
        icon: (
            <svg width="26" height="26" viewBox="0 0 32 32" fill="none">
                <rect x="7" y="4" width="18" height="24" rx="2.5" stroke="currentColor" strokeWidth="2" />
                <path d="M12 11h8M12 16h8M12 21h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We optimise important service and location pages so they clearly communicate your offering and the areas you genuinely serve.',
    },
    {
        title: 'Local Citations',
        span: 'md:col-span-2',
        icon: (
            <svg width="26" height="26" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="12" width="12" height="8" rx="4" stroke="currentColor" strokeWidth="2" />
                <rect x="16" y="12" width="12" height="8" rx="4" stroke="currentColor" strokeWidth="2" />
                <path d="M12 16h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We improve the consistency and accuracy of your business information across relevant local directories.',
    },
    {
        title: 'Reviews & Reputation',
        span: 'md:col-span-2',
        icon: (
            <svg width="26" height="26" viewBox="0 0 32 32" fill="none">
                <path d="M16 4l3 8 8 3-8 3-3 8-3-8-8-3 8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
        ),
        desc: 'We help establish a sustainable process for generating genuine reviews and turning experiences into trust signals.',
    },
    {
        title: 'Local Content',
        span: 'md:col-span-2',
        icon: (
            <svg width="26" height="26" viewBox="0 0 32 32" fill="none">
                <rect x="5" y="7" width="15" height="20" rx="2.5" stroke="currentColor" strokeWidth="2" />
                <rect x="13" y="3" width="15" height="20" rx="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M17 9h7M17 13h7M17 17h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We create useful location-focused content based on real customer needs, not dozens of repetitive city pages.',
    },
];

function ServiceCard({ item, index }: { item: typeof items[0]; index: number }) {
    const [hovered, setHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.07 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={`group relative rounded-3xl transition-colors duration-300 overflow-hidden ${item.span} ${item.featured ? '' : 'flex flex-col justify-between gap-5 p-6'}`}
            style={{
                background: item.featured ? CARD : hovered ? 'rgba(55,48,163,0.08)' : CARD,
                border: item.featured ? '1.5px solid rgba(55,48,163,0.25)' : hovered ? '1.5px solid rgba(55,48,163,0.55)' : '1.5px solid rgba(8,4,0,0.08)',
            }}
        >
            {item.featured ? (
                <>
                    <video
                        src="/videos/local-seo-gbp.mp4"
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div
                        className="absolute inset-0"
                        style={{ background: 'linear-gradient(180deg, rgba(8,4,0,0) 40%, rgba(8,4,0,0.85) 100%)' }}
                    />
                    <div className="relative flex flex-col gap-2.5 p-8 h-full justify-end">
                        <h3
                            className="text-2xl font-bold leading-snug"
                            style={{ fontFamily: CLASH, color: '#ffffff', letterSpacing: '0.01em' }}
                        >
                            {item.title}
                        </h3>
                        <p
                            className="text-base leading-relaxed"
                            style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.01em' }}
                        >
                            {item.desc}
                        </p>
                    </div>
                </>
            ) : (
                <>
                    <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 transition-all duration-300 group-hover:-rotate-6"
                        style={{
                            background: hovered ? '#080400' : 'rgba(55,48,163,0.07)',
                            border: hovered ? '1px solid #080400' : '1px solid rgba(55,48,163,0.16)',
                            color: hovered ? ACCENT_LIGHT : PURPLE,
                        }}
                    >
                        {item.icon}
                    </div>

                    <div className="flex flex-col gap-2.5">
                        <h3
                            className="text-lg font-bold leading-snug"
                            style={{ fontFamily: CLASH, color: INK, letterSpacing: '0.01em' }}
                        >
                            {item.title}
                        </h3>
                        <p
                            className="text-sm leading-relaxed"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.62)', letterSpacing: '0.01em' }}
                        >
                            {item.desc}
                        </p>
                    </div>
                </>
            )}
        </motion.div>
    );
}

export default function LocalSEOServices() {
    return (
        <section
            className="relative overflow-hidden py-20 md:py-28 px-6 md:px-16 lg:px-24"
            style={{ background: '#ffffff', borderRadius: '80px' }}
        >
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.035]"
                style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
            />

            <div className="max-w-[1200px] mx-auto flex flex-col gap-14 relative">
                <div className="flex flex-col gap-4">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: PURPLE, fontFamily: CLASH }}
                    >
                        What We Do
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: INK }}
                    >
                        Our Local SEO Services
                    </motion.h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-6 md:auto-rows-[1fr] gap-5">
                    {items.map((it, i) => (
                        <ServiceCard key={it.title} item={it} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
